import type { Event } from '@/payload-types'

/**
 * An event a public listing or detail page can render.
 * Drafts and incomplete admin saves stay on `Event`. Published pages require both dates.
 */
export type DisplayEvent = Event & {
  general: NonNullable<Event['general']> & {
    startAt: string
    endAt: string
  }
}

/**
 * True when the event has the start and end a public page renders.
 * Blank strings do not count.
 */
export function isDisplayEvent(event: Event): event is DisplayEvent {
  const startAt = event.general?.startAt
  const endAt = event.general?.endAt
  return (
    typeof startAt === 'string' &&
    startAt.length > 0 &&
    typeof endAt === 'string' &&
    endAt.length > 0
  )
}

/**
 * The event when it can be shown on a public listing or detail page, otherwise null.
 */
export function toDisplayEvent(event: Event): DisplayEvent | null {
  if (!isDisplayEvent(event)) return null
  return event
}

/**
 * Events from a query that have the dates a public listing renders.
 */
export function toDisplayEvents(events: Event[]): DisplayEvent[] {
  return events.flatMap((event) => {
    const displayEvent = toDisplayEvent(event)
    return displayEvent ? [displayEvent] : []
  })
}
